import type { VehicleRow, SpecRow, DimensionRow, SafetyRow } from "@/lib/vehicle-queries";
import type { NarrativeSection } from "@/lib/spec-narrative";
import { isDepthScoped } from "@/lib/depth-scope";
import { benchFor, SAFETY_COHORT, type Benchmark } from "@/lib/depth-benchmarks";

/**
 * Extended, blunt-voice analysis for the templates Semrush validated as worth
 * content investment. Answer first, explanation after; no hedging, no filler.
 * Legally or factually required caveats stay in — towing and safety data is
 * YMYL-adjacent.
 *
 * Differentiation comes from two mechanisms, not from padding:
 *  1. Fact selection — each page emits only the facts its own rows support, so
 *     a truck with payload, GCWR and six bed configs writes a different set of
 *     sentences from one with a single rating.
 *  2. Bucketed phrasing — every fact has several phrasings chosen by where the
 *     vehicle's numbers fall (capacity class, spread size, cycle bias), so the
 *     sentence skeleton changes with the data, not with a random seed.
 */

export interface DepthInput {
  fullName: string;
  makeSlug: string;
  modelSlug: string;
  dataType: string;
  vehicles: VehicleRow[];
  specs: SpecRow[];
  dimensions?: DimensionRow[];
  safety?: SafetyRow[];
}

const n0 = (v: number) => Math.round(v).toLocaleString();
const n1 = (v: number) => (Math.round(v * 10) / 10).toLocaleString();

/** A candidate sentence. Only supported facts are generated. */
interface Fact {
  id: string;
  text: string;
}

/**
 * Pick one phrasing from a pool using a value derived from the vehicle's own
 * record (row counts, years, headline figure). Deterministic per page, and it
 * keeps advisory sentences — which carry the same meaning everywhere — from
 * being byte-identical across an entire cluster of pages.
 */
function variant(pool: string[], seed: number): string {
  return pool[Math.abs(Math.round(seed)) % pool.length]!;
}

/** Sourcing sentence built from the rows actually behind this page. */
function sourcing(rows: Array<{ source: string; verified_at?: string; year: number }>): Fact | null {
  if (rows.length === 0) return null;
  const srcs = Array.from(new Set(rows.map((r) => r.source).filter(Boolean)));
  const dates = rows.map((r) => r.verified_at).filter(Boolean) as string[];
  const latest = dates.sort().slice(-1)[0];
  const yrs = Array.from(new Set(rows.map((r) => r.year)));
  return {
    id: "sourcing",
    text:
      `Source${srcs.length === 1 ? "" : "s"} for these ${rows.length} row${rows.length === 1 ? "" : "s"}: ${srcs.join(", ") || "manufacturer filings"}` +
      (latest ? `, last verified ${latest.slice(0, 10)}` : "") +
      `, covering ${yrs.length} model year${yrs.length === 1 ? "" : "s"}.`,
  };
}

function section(heading: string, facts: Fact[]): NarrativeSection[] {
  const body = facts
    .map((f) => f.text.trim())
    .filter(Boolean)
    .join(" ");
  return body ? [{ heading, body }] : [];
}

/**
 * Comparative facts against the rest of our own dataset. Rank, cohort median
 * and the nearest rival above and below are different for every model, which
 * is what stops two trucks with similar raw numbers reading the same way.
 */
function comparative(
  name: string,
  b: Benchmark | null,
  unit: (v: number) => string,
  cohort: string,
  higherIsBetter = true,
): Fact[] {
  if (!b) return [];
  const out: Fact[] = [];
  const pct = Math.round((1 - (b.rank - 1) / Math.max(1, b.n - 1)) * 100);
  const vsMed = b.value - b.median;
  const place =
    b.rank === 1
      ? `The ${name} leads every ${cohort} in our data`
      : b.rank <= 3
        ? `That puts the ${name} ${b.rank === 2 ? "second" : "third"} of ${b.n} ${cohort}s we hold data for`
        : b.rank <= b.n * 0.25
          ? `That is rank ${b.rank} of ${b.n} ${cohort}s here — top quartile`
          : b.rank >= b.n * 0.75
            ? `That is rank ${b.rank} of ${b.n} ${cohort}s here — bottom quartile`
            : `That is rank ${b.rank} of ${b.n} ${cohort}s in this dataset, ${pct}th percentile`;
  out.push({
    id: "rank",
    text:
      `${place}. The cohort median is ${unit(b.median)}, so this vehicle sits ` +
      (Math.abs(vsMed) < 0.5
        ? `right on it.`
        : `${unit(Math.abs(vsMed))} ${vsMed > 0 ? "above" : "below"} the middle of the field${(vsMed > 0) === higherIsBetter ? "" : " — a real deficit, not a rounding difference"}.`),
  });
  if (b.above) {
    out.push({
      id: "above",
      text: `Next rung up is the ${b.above[0]} at ${unit(b.above[1])}, ${unit(Math.abs(b.above[1] - b.value))} clear of this one.`,
    });
  }
  if (b.below) {
    out.push({
      id: "below",
      text: `Immediately behind: the ${b.below[0]} at ${unit(b.below[1])}.`,
    });
  }
  if (b.ties && b.ties.length) {
    out.push({
      id: "ties",
      text: `${b.ties.join(", ")} ${b.ties.length === 1 ? "matches" : "match"} it exactly — that figure is a segment convention, not an engineering ceiling.`,
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Towing capacity                                                     */
/* ------------------------------------------------------------------ */

function towClass(max: number) {
  if (max >= 20000)
    return {
      tier: "heavy-duty",
      hitch: "Class V (gooseneck or fifth-wheel)",
      loads: "fifth-wheel campers, equipment trailers, three-horse gooseneck rigs",
      verdict: "This is commercial-grade capacity. Bumper-pull hitches are the limiting factor long before the truck is.",
    };
  if (max >= 12000)
    return {
      tier: "three-quarter-ton",
      hitch: "Class V weight-distributing",
      loads: "large travel trailers, car haulers, two-horse trailers with living quarters",
      verdict: "Anything this pulls needs trailer brakes and a weight-distributing hitch. The tow rating is not the hard part; stopping is.",
    };
  if (max >= 8000)
    return {
      tier: "full-size half-ton",
      hitch: "Class IV",
      loads: "most travel trailers under 30 ft, a two-car open trailer, a large boat",
      verdict: "Comfortable for weekend towing. Sustained mountain grades at max weight will heat the transmission — watch temps, not the rating.",
    };
  if (max >= 5000)
    return {
      tier: "mid-size",
      hitch: "Class III",
      loads: "a small travel trailer, a 20 ft boat, a single-axle utility trailer",
      verdict: "Enough for occasional towing, not for a season of it. Loaded to the rating this vehicle is working hard.",
    };
  if (max >= 3500)
    return {
      tier: "light-duty crossover",
      hitch: "Class III",
      loads: "a pop-up camper, a pair of jet skis, a small enclosed trailer",
      verdict: "Treat the rating as a ceiling, not a target. Transmission cooling is the weak point on unibody platforms.",
    };
  return {
    tier: "light-trailer",
    hitch: "Class I or II",
    loads: "a bike rack, a small open utility trailer, a jet ski",
    verdict: "Nothing that needs trailer brakes. Adding a hitch does not change what the drivetrain was engineered to pull.",
  };
}

function towing(input: DepthInput): NarrativeSection[] {
  const rows = input.specs.filter((s) => (s.towing_capacity_lbs ?? 0) > 0);
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort(
    (a, b) => (b.towing_capacity_lbs ?? 0) - (a.towing_capacity_lbs ?? 0),
  );
  const top = sorted[0]!;
  const bottom = sorted[sorted.length - 1]!;
  const max = top.towing_capacity_lbs!;
  const min = bottom.towing_capacity_lbs!;
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const latestYear = years[years.length - 1]!;
  const latestMax = Math.max(
    ...rows.filter((r) => r.year === latestYear).map((r) => r.towing_capacity_lbs ?? 0),
  );
  const cls = towClass(max);
  const spreadPct = max ? Math.round(((max - min) / max) * 100) : 0;

  /* --- Section 1: the number --- */
  const lead: Fact[] = [];
  lead.push({
    id: "max",
    text: `The ${name} tows ${n0(max)} lbs at most${top.towing_config ? ` (${top.towing_config})` : ""}, on the ${top.year} model year. That is ${cls.tier} capacity — ${cls.loads}.`,
  });
  if (spreadPct === 0) {
    lead.push({
      id: "uniform",
      text: `Every configuration on file carries the same rating, so trim and engine choice do not change what it pulls.`,
    });
  } else if (spreadPct >= 50) {
    lead.push({
      id: "spread-wide",
      text: `The weakest configuration tows ${n0(min)} lbs. That is ${spreadPct}% less than the headline — over half the advertised capacity disappears with the wrong engine or axle. Never shop this model on the brochure number.`,
    });
  } else if (spreadPct >= 20) {
    lead.push({
      id: "spread-mid",
      text: `The lowest-rated version tows ${n0(min)} lbs, a ${n0(max - min)} lb gap. Configuration decides roughly ${spreadPct}% of the capacity here.`,
    });
  } else {
    lead.push({
      id: "spread-narrow",
      text: `The floor is ${n0(min)} lbs — only ${spreadPct}% under the peak. Any version of this vehicle tows within a trailer-load of any other.`,
    });
  }
  if (latestMax < max * 0.98) {
    lead.push({
      id: "declined",
      text: `The ${latestYear} model tops out at ${n0(latestMax)} lbs, ${n0(max - latestMax)} lbs below the ${top.year} peak. On towing alone, the older truck is the stronger buy.`,
    });
  } else if (latestMax > max * 0.98 && latestYear !== top.year) {
    lead.push({
      id: "held",
      text: `The ${latestYear} model matches that ceiling at ${n0(latestMax)} lbs. Capacity has held, so buy on price and condition instead of model year.`,
    });
  }
  lead.push(...comparative(name, benchFor("towing-capacity", input.makeSlug, input.modelSlug), (v) => `${n0(v)} lbs`, "tow-rated vehicle"));
  lead.push({ id: "hitch", text: `Hitch requirement at full weight: ${cls.hitch}.` });

  /* --- Section 2: usable weight --- */
  const safe = Math.round((max * 0.8) / 50) * 50;
  const tongueLo = Math.round((max * 0.1) / 10) * 10;
  const tongueHi = Math.round((max * 0.15) / 10) * 10;
  const payloads = rows.map((r) => r.payload_capacity_lbs ?? 0).filter((v) => v > 0);
  const maxPayload = payloads.length ? Math.max(...payloads) : null;
  const usable: Fact[] = [];
  usable.push({
    id: "eighty",
    text: `Plan around ${n0(safe)} lbs, not ${n0(max)}. The rating assumes one driver and nothing else — no passengers, no cargo, no roof rack, no aftermarket bumper.`,
  });
  usable.push({
    id: "tongue",
    text: `Tongue weight on a ${n0(safe)} lb trailer is ${n0(tongueLo)}–${n0(tongueHi)} lbs, and that lands on payload, not on the tow rating.`,
  });
  if (maxPayload) {
    const left = Math.max(0, maxPayload - tongueHi);
    usable.push(
      left < 600
        ? {
            id: "payload-tight",
            text: `Payload peaks at ${n0(maxPayload)} lbs. After tongue weight you have ${n0(left)} lbs for people and gear — three adults and a cooler puts this vehicle over its GVWR before the trailer is loaded. Payload, not tow rating, is the real limit here.`,
          }
        : left < 1200
          ? {
              id: "payload-mid",
              text: `Payload peaks at ${n0(maxPayload)} lbs, leaving about ${n0(left)} lbs after tongue weight. A full cabin of passengers eats most of that.`,
            }
          : {
              id: "payload-roomy",
              text: `Payload peaks at ${n0(maxPayload)} lbs, so roughly ${n0(left)} lbs remain after tongue weight — a full cab and a loaded bed still fits under the limit.`,
            },
    );
  } else {
    usable.push({
      id: "payload-missing",
      text: `Payload is not published for every configuration in this record. The yellow door-jamb sticker on the specific vehicle is the governing number.`,
    });
  }
  if (max >= 3000) {
    usable.push({
      id: "brakes",
      text: `Trailers over 3,000 lbs need their own brakes in most states, and this vehicle is rated well past that, so budget for a brake controller.`,
    });
  }
  const seed = rows.length * 7 + years.length * 3 + Math.round(max / 100);
  usable.push({ id: "verdict", text: cls.verdict });
  usable.push({
    id: "caveat",
    text: variant(
      [
        `Ratings are manufacturer figures, measured to SAE J2807 where the maker applies it. Check the door-jamb label for the exact VIN before hitching up.`,
        `These are certified manufacturer ratings, not tested results. The governing number for any individual truck is on its door-jamb label and in its owner's manual.`,
        `Manufacturers publish these under SAE J2807 where they adopt it; older model years predate the standard and are not directly comparable to newer ones.`,
        `Treat published ratings as the maximum a perfectly specified example can do. Options, axle ratio and even wheel size move the real number down.`,
      ],
      seed,
    ),
  });

  /* --- Section 3: configuration --- */
  const configs = Array.from(
    new Set(rows.map((r) => (r.towing_config ?? r.trim ?? "").trim()).filter(Boolean)),
  );
  const gcwr = rows.map((r) => r.gcwr_lbs ?? 0).filter((v) => v > 0);
  const conf: Fact[] = [];
  conf.push({
    id: "count",
    text: `${rows.length} rated configuration${rows.length === 1 ? "" : "s"} across ${years.length} model year${years.length === 1 ? "" : "s"} (${years[0]}–${years[years.length - 1]}).`,
  });
  if (configs.length > 3) {
    conf.push({
      id: "configs-many",
      text: `The record splits by ${configs.slice(0, 6).join(", ")}${configs.length > 6 ? ` and ${configs.length - 6} more` : ""}. With this many variants, a single quoted figure for the model is meaningless — match the row to the drivetrain and package on the window sticker.`,
    });
  } else if (configs.length > 1) {
    conf.push({
      id: "configs-few",
      text: `Two paths through the range: ${configs.join(" and ")}. Everything else is shared.`,
    });
  }
  if (gcwr.length) {
    conf.push({
      id: "gcwr",
      text: `Gross combined weight rating peaks at ${n0(Math.max(...gcwr))} lbs — truck plus trailer plus everything in both. That ceiling binds before the tow rating does on a loaded rig.`,
    });
  }
  const bedRows = input.specs.filter((r) => (r.bed_length_in ?? 0) > 0);
  if (bedRows.length) {
    const bl = Math.max(...bedRows.map((r) => r.bed_length_in ?? 0));
    conf.push({
      id: "bed",
      text: `It also carries a bed — ${n1(bl)} in at the longest — so payload and tongue weight compete with cargo, not just passengers. Loading the bed and the hitch on the same trip is what puts these over GVWR.`,
    });
  }
  const payloadSpread = payloads.length > 1 ? Math.max(...payloads) - Math.min(...payloads) : 0;
  if (payloadSpread > 400) {
    conf.push({
      id: "payload-spread",
      text: `Payload itself swings ${n0(payloadSpread)} lbs across these configurations, so two examples with the same tow rating are not the same tool.`,
    });
  }
  conf.push({
    id: "package",
    text: variant(
      [
        `A bolt-on hitch does not raise capacity. The rating is built on cooling, axle ratio, brakes and frame reinforcement fitted at the factory.`,
        `Capacity comes from the tow package — transmission and engine oil coolers, a numerically higher axle ratio, upgraded brakes — not from the receiver bolted under the bumper.`,
        `Adding a receiver to a vehicle built without the tow package buys you a place to hang a trailer, not the rating that goes with it.`,
      ],
      rows.length + years.length,
    ),
  });
  const src = sourcing(rows as any);
  if (src) conf.push(src);

  return [
    ...section(`Max towing capacity: ${n0(max)} lbs`, lead),
    ...section("What it tows loaded", usable),
    ...section("Which configuration hits the number", conf),
  ];
}

/* ------------------------------------------------------------------ */
/* MPG                                                                 */
/* ------------------------------------------------------------------ */

function mpgClass(v: number) {
  if (v >= 45) return { tier: "hybrid-class", peer: "a Prius sits near 55 MPG" };
  if (v >= 33) return { tier: "efficient", peer: "the 2024 US new-car average is about 28 MPG" };
  if (v >= 26) return { tier: "average", peer: "the 2024 US new-car average is about 28 MPG" };
  if (v >= 20) return { tier: "thirsty", peer: "a mid-size sedan returns 32 MPG on the same cycle" };
  if (v >= 15) return { tier: "truck-class", peer: "a half-ton V8 pickup runs 17–20 MPG" };
  return { tier: "performance-class", peer: "anything under 15 MPG combined is supercar or heavy-truck territory" };
}

function mpg(input: DepthInput): NarrativeSection[] {
  const rows = input.vehicles.filter((v) => (v.mpg_combined ?? 0) > 0);
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort((a, b) => (b.mpg_combined ?? 0) - (a.mpg_combined ?? 0));
  const best = sorted[0]!;
  const worst = sorted[sorted.length - 1]!;
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const latestYear = years[years.length - 1]!;
  const latest = rows.filter((r) => r.year === latestYear);
  const latestAvg = latest.reduce((s, r) => s + (r.mpg_combined ?? 0), 0) / Math.max(1, latest.length);
  const GAS = 3.5;
  const cost15k = (m: number) => Math.round((15000 / m) * GAS);
  const cls = mpgClass(best.mpg_combined!);

  const lead: Fact[] = [];
  lead.push({
    id: "best",
    text: `Best combined figure for the ${name} is ${best.mpg_combined} MPG (${best.year}${best.engine_desc ? `, ${best.engine_desc}` : best.trim ? `, ${best.trim}` : ""}) — ${cls.tier} for its era, where ${cls.peer}.`,
  });
  const swing = cost15k(worst.mpg_combined!) - cost15k(best.mpg_combined!);
  if (worst.mpg_combined !== best.mpg_combined) {
    lead.push({
      id: "worst",
      text: `The weakest is ${worst.mpg_combined} MPG (${worst.year}${worst.trim ? `, ${worst.trim}` : ""}). At 15,000 miles a year and $3.50/gal that is $${n0(cost15k(best.mpg_combined!))} against $${n0(cost15k(worst.mpg_combined!))} — a $${n0(swing)} annual swing, ${swing >= 700 ? "enough to outweigh most price differences between two used examples" : "small enough that condition matters more than which one you pick"}.`,
    });
  } else {
    lead.push({
      id: "flat",
      text: `Every configuration on file returns the same combined figure, so fuel cost is fixed at roughly $${n0(cost15k(best.mpg_combined!))} a year at 15,000 miles and $3.50/gal.`,
    });
  }
  lead.push(
    ...comparative(
      name,
      benchFor("mpg", input.makeSlug, input.modelSlug),
      (v) => `${n1(v)} MPG`,
      benchFor("mpg", input.makeSlug, input.modelSlug)?.cls?.toLowerCase().replace(/ 2wd| 4wd/, "") ?? "vehicle",
    ),
  );
  lead.push({
    id: "latest",
    text: `The ${latestYear} model averages ${n1(latestAvg)} MPG across ${latest.length} configuration${latest.length === 1 ? "" : "s"}.` +
      (latestAvg < best.mpg_combined! - 0.5
        ? ` The efficiency peak is the ${best.year} car, not the newest one — usually a weight or all-wheel-drive change rather than a worse engine.`
        : ` That is the high-water mark for the nameplate.`),
  });

  const cycle: Fact[] = [];
  const city = best.mpg_city;
  const hwy = best.mpg_hwy;
  if (city && hwy) {
    const gap = hwy - city;
    cycle.push({ id: "split", text: `${city} city / ${hwy} highway on the ${best.year} ${name}.` });
    cycle.push(
      gap >= 8
        ? {
            id: "hwy-heavy",
            text: `A ${n0(gap)} MPG gap makes this strongly highway-biased. Short urban trips will land nearer ${city} than the combined number, and a stop-start commute can cost you a third of the advertised economy.`,
          }
        : gap >= 3
          ? {
              id: "hwy-mild",
              text: `The ${n0(gap)} MPG gap is the normal gasoline pattern. Your mix moves the result by a few MPG, not by a category.`,
            }
          : gap <= 0
            ? {
                id: "city-favoured",
                text: `City beats highway by ${n0(Math.abs(gap))} MPG — hybrid behaviour. Regenerative braking earns more in traffic than steady cruising does, so urban drivers get the best of this powertrain.`,
              }
            : {
                id: "flat-cycle",
                text: `Only ${n0(gap)} MPG separates the cycles, so driving mix barely changes the outcome.`,
              },
    );
  }
  cycle.push({
    id: "real-world",
    text: variant(
      [
        `Expect 10–15% under the EPA figure in real use. Cold starts, roof boxes and 80 mph cruising widen that gap.`,
        `Owners typically report a few MPG under these numbers. The test cycle uses a warm engine, mild temperatures and gentle throttle — three things a commute rarely provides.`,
        `Winter fuel blends and sub-freezing warm-up cost 10–20% on their own, so a cold-climate owner should read the city figure as the realistic ceiling.`,
      ],
      rows.length + years.length,
    ),
  });
  if (best.annual_fuel_cost) {
    cycle.push({
      id: "epa-cost",
      text: `EPA's own annual cost estimate for the best configuration is $${n0(best.annual_fuel_cost)}.`,
    });
  }
  if (best.co2_tailpipe_gpm) {
    cycle.push({
      id: "co2",
      text: `Tailpipe CO₂ is ${n0(best.co2_tailpipe_gpm)} g/mile, about ${n1((best.co2_tailpipe_gpm * 15000) / 1_000_000)} metric tons a year.`,
    });
  }

  const spread: Fact[] = [];
  const drives = Array.from(new Set(rows.map((r) => r.drive).filter(Boolean))) as string[];
  const fuels = Array.from(new Set(rows.map((r) => r.fuel_type).filter(Boolean))) as string[];
  const engines = Array.from(new Set(rows.map((r) => r.engine_desc).filter(Boolean))) as string[];
  spread.push({
    id: "count",
    text: `${rows.length} EPA-tested configuration${rows.length === 1 ? "" : "s"} across ${years[0]}–${years[years.length - 1]}.`,
  });
  if (drives.length > 1) {
    spread.push({
      id: "drive",
      text: `Drivetrain is the biggest lever: ${drives.join(", ")} versions are certified separately and rarely land within 2 MPG of each other.`,
    });
  } else if (drives[0]) {
    spread.push({
      id: "drive-single",
      text: `Every configuration here is ${drives[0]}, so engine and transmission account for the entire spread.`,
    });
  }
  if (engines.length > 3) {
    spread.push({
      id: "engines",
      text: `${engines.length} distinct engine descriptions appear in the record, which is why quoting one figure for the nameplate misleads.`,
    });
  }
  if (fuels.length > 1) {
    spread.push({ id: "fuels", text: `Fuel types on file: ${fuels.join(", ")}.` });
  }
  spread.push({
    id: "caveat",
    text: variant(
      [
        `Figures are EPA submissions filed per configuration, not marketing claims. A missing year means no filing, not a zero.`,
        `Every number here comes from the manufacturer's certification filing to the EPA for that exact configuration — the same data behind the window sticker.`,
        `EPA revised its test methodology in 2008; ratings from before then read several MPG higher than the same car would score today.`,
      ],
      rows.length + Math.round(best.mpg_combined!),
    ),
  });
  const msrc = sourcing(rows.map((r) => ({ source: r.source, verified_at: r.verified_at, year: r.year })));
  if (msrc) spread.push(msrc);

  return [
    ...section(`Best combined MPG: ${best.mpg_combined} MPG`, lead),
    ...section("City versus highway", cycle),
    ...section("What drives the spread", spread),
  ];
}

/* ------------------------------------------------------------------ */
/* Truck bed dimensions                                                */
/* ------------------------------------------------------------------ */

function bedDimensions(input: DepthInput): NarrativeSection[] {
  const rows = input.specs.filter((s) => (s.bed_length_in ?? 0) > 0);
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort((a, b) => (b.bed_length_in ?? 0) - (a.bed_length_in ?? 0));
  const longest = sorted[0]!;
  const shortest = sorted[sorted.length - 1]!;
  const configs = Array.from(
    new Set(rows.map((r) => (r.bed_config ?? r.trim ?? "").trim()).filter(Boolean)),
  );
  const wells = rows.map((r) => r.bed_width_between_wheelwells_in ?? 0).filter((v) => v > 0);
  const maxWell = wells.length ? Math.max(...wells) : null;
  const vols = rows.map((r) => r.bed_volume_cuft ?? 0).filter((v) => v > 0);
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const L = longest.bed_length_in!;

  const lead: Fact[] = [];
  lead.push({
    id: "longest",
    text: `The longest ${name} bed measures ${n1(L)} in — ${n1(L / 12)} ft${longest.bed_config ? `, the ${longest.bed_config}` : ""}.`,
  });
  if (shortest.bed_length_in! !== L) {
    const d = L - shortest.bed_length_in!;
    lead.push(
      d >= 24
        ? {
            id: "range-wide",
            text: `The short bed drops to ${n1(shortest.bed_length_in!)} in — ${n1(d)} in shorter, more than two feet of usable floor. Cab choice usually forces this decision, not preference.`,
          }
        : {
            id: "range-narrow",
            text: `The shortest bed is ${n1(shortest.bed_length_in!)} in, only ${n1(d)} in down. Both configurations carry the same things.`,
          },
    );
  } else {
    lead.push({ id: "single-bed", text: `One bed length is offered across the whole record.` });
  }
  if (longest.bed_width_in) {
    lead.push({
      id: "width",
      text: `Width at the rails is ${n1(longest.bed_width_in)} in${longest.bed_depth_in ? `, depth ${n1(longest.bed_depth_in)} in` : ""}.`,
    });
  }
  lead.push(
    ...comparative(name, benchFor("bed-dimensions", input.makeSlug, input.modelSlug), (v) => `${n1(v)} in`, "pickup"),
  );
  if (vols.length) {
    const v = Math.max(...vols);
    lead.push({
      id: "volume",
      text: `Peak cargo volume is ${n1(v)} cu ft — ${v >= 70 ? "about the load space of a large cargo van's floor" : v >= 50 ? "roughly a mid-size SUV with its seats folded" : "compact-truck territory"}.`,
    });
  }

  const sheet: Fact[] = [];
  if (maxWell) {
    sheet.push(
      maxWell >= 50
        ? {
            id: "well-clear",
            text: `Yes, with room to spare: ${n1(maxWell)} in between the wheel wells against a 48 in sheet, so plywood and drywall lie flat and still leave ${n1(maxWell - 48)} in of slack.`,
          }
        : maxWell >= 48
          ? {
              id: "well-tight",
              text: `Yes, barely: ${n1(maxWell)} in between the wheel wells clears a 48 in sheet with ${n1(maxWell - 48)} in to spare. A bed liner can take that back.`,
            }
          : {
              id: "well-fail",
              text: `No. ${n1(maxWell)} in between the wheel wells is ${n1(48 - maxWell)} in short of a 48 in sheet. Sheet goods ride on top of the wells or go in at an angle.`,
            },
    );
  } else {
    sheet.push({
      id: "well-unknown",
      text: `Wheel-well spacing is not published for every configuration here. Measure between the wells before assuming a 48 in sheet drops flat.`,
    });
  }
  sheet.push(
    L >= 96
      ? {
          id: "len-ok",
          text: `Length is not the constraint: ${n1(L)} in takes a full 8 ft sheet with the tailgate closed.`,
        }
      : {
          id: "len-short",
          text: `An 8 ft sheet overhangs by ${n1(96 - L)} in with the tailgate up, so it travels with the gate down and a red flag.`,
        },
  );
  sheet.push({
    id: "liner",
    text: variant(
      [
        `A drop-in liner costs 1–2 in of width and depth. Measure the actual truck before ordering a tonneau, rack or slide-out.`,
        `Spray-in liners lose almost nothing; drop-in plastic liners lose an inch or more each side, which is the difference between a sheet lying flat and not.`,
        `Bedside tie-down rails and toolboxes eat into these figures too — the published width is bare metal to bare metal.`,
      ],
      rows.length + Math.round(L),
    ),
  });

  const cover: Fact[] = [];
  cover.push({
    id: "count",
    text: `${rows.length} bed configuration${rows.length === 1 ? "" : "s"} across ${years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : years[0]}, one row per bed and cab combination.`,
  });
  if (configs.length > 2) {
    cover.push({
      id: "configs",
      text: `Beds on file: ${configs.slice(0, 5).join(", ")}${configs.length > 5 ? ` and ${configs.length - 5} more` : ""}. The longest bed is rarely orderable with the largest cab.`,
    });
  } else if (configs.length) {
    cover.push({ id: "configs-few", text: `Beds on file: ${configs.join(" and ")}.` });
  }
  cover.push({
    id: "caveat",
    text: variant(
      [
        `Figures are manufacturer specifications as filed — nominal inside dimensions, not measurements we took.`,
        `These are published inside dimensions. Manufacturers measure at the floor; real usable space narrows toward the rails on most boxes.`,
        `Nominal bed names round hard: a "6.5-ft bed" is rarely exactly 78 in, which is why the measured column matters more than the marketing label.`,
      ],
      rows.length + configs.length,
    ),
  });
  const bsrc = sourcing(rows as any);
  if (bsrc) cover.push(bsrc);

  return [
    ...section(`Longest bed: ${n1(L)} inches`, lead),
    ...section("Does a 4x8 sheet fit flat", sheet),
    ...section("Configurations covered", cover),
  ];
}


/* ------------------------------------------------------------------ */
/* Fuel tank capacity                                                  */
/* ------------------------------------------------------------------ */

function fuelTank(input: DepthInput): NarrativeSection[] {
  const rows = input.specs.filter((s) => (s.fuel_tank_gal ?? 0) > 0);
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort((a, b) => (b.fuel_tank_gal ?? 0) - (a.fuel_tank_gal ?? 0));
  const big = sorted[0]!;
  const small = sorted[sorted.length - 1]!;
  const gal = big.fuel_tank_gal!;
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const optional = rows.map((r) => r.fuel_tank_gal_optional ?? 0).filter((v) => v > 0);

  // Range comes from this model's own EPA figures, not from an assumption.
  const combined = input.vehicles.map((v) => v.mpg_combined ?? 0).filter((v) => v > 0);
  const hwy = input.vehicles.map((v) => v.mpg_hwy ?? 0).filter((v) => v > 0);
  const bestC = combined.length ? Math.max(...combined) : null;
  const worstC = combined.length ? Math.min(...combined) : null;
  const bestH = hwy.length ? Math.max(...hwy) : null;

  const lead: Fact[] = [];
  lead.push({
    id: "gal",
    text: `The ${name} holds ${n1(gal)} gallons${big.trim ? ` (${big.trim})` : ""}.`,
  });
  if (small.fuel_tank_gal! !== gal) {
    lead.push({
      id: "spread",
      text: `Smaller-tank configurations carry ${n1(small.fuel_tank_gal!)} gal — ${n1(gal - small.fuel_tank_gal!)} gal less, and on a long drive that is a whole extra stop.`,
    });
  }
  if (optional.length) {
    lead.push({
      id: "optional",
      text: `An extended tank of ${n1(Math.max(...optional))} gal was offered, which is the option to look for on a used example if you tow or commute long distances.`,
    });
  }
  lead.push(
    ...comparative(name, benchFor("fuel-tank-capacity", input.makeSlug, input.modelSlug), (v) => `${n1(v)} gal`, "vehicle we hold tank data for"),
  );

  const range: Fact[] = [];
  if (bestC && worstC) {
    const rBest = Math.round(gal * bestC);
    const rWorst = Math.round(gal * worstC);
    range.push({
      id: "range",
      text: `Full-tank range works out at ${n0(rWorst)}–${n0(rBest)} miles on EPA combined figures (${worstC}–${bestC} MPG across the configurations on file).`,
    });
    if (bestH) {
      range.push({
        id: "hwy-range",
        text: `On the highway cycle at ${bestH} MPG the best version covers ${n0(Math.round(gal * bestH))} miles between fills.`,
      });
    }
    const usable = Math.round(gal * 0.9 * worstC);
    range.push({
      id: "reserve",
      text: variant(
        [
          `Plan refuelling at ${n0(usable)} miles, not the theoretical figure. The low-fuel light typically leaves 2–3 gallons, and running a tank dry pushes debris and heat through the in-tank pump.`,
          `Usable range is nearer ${n0(usable)} miles. The last tenth of the tank sits below the pump pickup on hills and hard cornering.`,
          `Treat ${n0(usable)} miles as the working number. Cold weather, roof loads and a 75 mph cruise each cost 10–20% against the EPA figure.`,
        ],
        Math.round(gal * 3) + rows.length + (bestC ?? 0),
      ),
    });
    range.push({
      id: "cost",
      text: variant(
        [
          `A fill from empty costs about $${n0(gal * 3.5)} at $3.50/gal — roughly ${Math.round(15000 / bestC / gal)} fills a year at 15,000 miles.`,
          `At $3.50/gal that is $${n0(gal * 3.5)} a tank and $${n0((15000 / bestC) * 3.5)} a year over 15,000 miles.`,
          `Annual fuel spend at 15,000 miles and $3.50/gal: $${n0((15000 / bestC) * 3.5)}, across about ${Math.round(15000 / bestC / gal)} tanks.`,
        ],
        Math.round(gal * 7) + (worstC ?? 0) * 3 + rows.length,
      ),
    });
  } else {
    range.push({
      id: "no-epa",
      text: `We hold no EPA economy filing for this model, so range depends entirely on the configuration in front of you: divide observed MPG into ${n1(gal)} gallons.`,
    });
  }

  const cover: Fact[] = [];
  cover.push({
    id: "count",
    text: `${rows.length} configuration${rows.length === 1 ? "" : "s"} on file across ${years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : years[0]}.`,
  });
  cover.push({
    id: "caveat",
    text: variant(
      [
        `Nominal capacity is not usable capacity. Tank shape, fill-neck design and the pump's auto shut-off mean you rarely put the full figure in.`,
        `Manufacturers quote total volume; the fuel you can actually load is typically half a gallon to a gallon less depending on how level the ground is.`,
        `Four-wheel-drive versions occasionally carry a smaller tank than two-wheel-drive ones to clear the transfer case, so match the row to the drivetrain.`,
      ],
      rows.length + Math.round(gal),
    ),
  });
  const src = sourcing(rows as any);
  if (src) cover.push(src);

  return [
    ...section(`Fuel tank: ${n1(gal)} gallons`, lead),
    ...section("How far that goes", range),
    ...section("Configurations and caveats", cover),
  ];
}

/* ------------------------------------------------------------------ */
/* Exterior dimensions                                                 */
/* ------------------------------------------------------------------ */

function dimensions(input: DepthInput): NarrativeSection[] {
  const rows = (input.dimensions ?? []).filter((d) => (d.length_in ?? 0) > 0);
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort((a, b) => (b.length_in ?? 0) - (a.length_in ?? 0));
  const longest = sorted[0]!;
  const shortest = sorted[sorted.length - 1]!;
  const L = longest.length_in!;
  const W = longest.width_in;
  const H = longest.height_in;
  const WB = longest.wheelbase_in;
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);

  const lead: Fact[] = [];
  lead.push({
    id: "lwh",
    text:
      `The ${name} measures ${n1(L)} in long${W ? ` by ${n1(W)} in wide` : ""}${H ? ` by ${n1(H)} in tall` : ""}` +
      `${WB ? `, on a ${n1(WB)} in wheelbase` : ""} — ${n1(L / 12)} ft of vehicle.`,
  });
  // A standard US garage is 18–20 ft deep and a parking bay is 16–18 ft.
  lead.push(
    L >= 216
      ? {
          id: "garage-tight",
          text: `At ${n1(L / 12)} ft it will not fit an 18 ft garage bay with the door closed, and it overhangs a standard 16 ft parking space by ${n1((L - 192) / 12)} ft.`,
        }
      : L >= 192
        ? {
            id: "garage-ok",
            text: `That clears an 18 ft garage with ${n1((216 - L) / 12)} ft to spare but fills a standard parking bay end to end.`,
          }
        : {
            id: "garage-easy",
            text: `Short enough to park anywhere: ${n1((192 - L) / 12)} ft of slack in a standard 16 ft bay.`,
          },
  );
  if (W) {
    lead.push(
      W >= 80
        ? {
            id: "width-wide",
            text: `Body width of ${n1(W)} in — before mirrors — makes narrow multi-storey ramps and older garage doors the real constraint, not length.`,
          }
        : {
            id: "width-ok",
            text: `${n1(W)} in across the body leaves usable door clearance in a standard 8.5 ft bay.`,
          },
    );
  }
  lead.push(
    ...comparative(name, benchFor("dimensions", input.makeSlug, input.modelSlug), (v) => `${n1(v)} in`, "vehicle in our dimensions table"),
  );

  const change: Fact[] = [];
  if (shortest.length_in! !== L) {
    const grew = longest.year > shortest.year;
    change.push({
      id: "growth",
      text: `The record runs from ${n1(shortest.length_in!)} in (${shortest.year}) to ${n1(L)} in (${longest.year}) — ${n1(L - shortest.length_in!)} in of difference${grew ? `, so the nameplate has grown` : `, with the largest version not being the newest`}.`,
    });
  }
  if (WB) {
    const ratio = L / WB;
    change.push(
      ratio >= 1.75
        ? {
            id: "overhang",
            text: `Wheelbase is ${n1(WB)} in against ${n1(L)} in overall, so ${n1(L - WB)} in sits outside the axles. Long overhangs scrape on steep driveways and ramps.`,
          }
        : {
            id: "wheelbase-long",
            text: `A ${n1(WB)} in wheelbase under ${n1(L)} in of body means short overhangs — better approach and departure angles, worse turning circle.`,
          },
    );
  }
  const weights = rows.map((r) => r.curb_weight_lbs ?? 0).filter((v) => v > 0);
  if (weights.length) {
    change.push({
      id: "weight",
      text: `Curb weight across these configurations runs ${n0(Math.min(...weights))}–${n0(Math.max(...weights))} lbs.`,
    });
  }

  const cover: Fact[] = [];
  cover.push({
    id: "count",
    text: variant(
      [
        `${rows.length} measured configuration${rows.length === 1 ? "" : "s"}, ${years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : years[0]}.`,
        `We hold ${rows.length} dimensioned row${rows.length === 1 ? "" : "s"} for this nameplate across ${years.length} model year${years.length === 1 ? "" : "s"}.`,
        `Measurements come from ${rows.length} configuration${rows.length === 1 ? "" : "s"}, newest ${years[years.length - 1]}.`,
      ],
      rows.length * 3 + Math.round(L) + years.length,
    ),
  });
  cover.push({
    id: "caveat",
    text: variant(
      [
        `Published dimensions exclude mirrors and roof aerials. Add 8–10 in of width for folded mirrors when you are measuring a garage.`,
        `Height figures assume the standard suspension and roof. Rails, crossbars and a raised air suspension setting each add an inch or more.`,
        `Manufacturers measure at curb weight with no load. A fully loaded vehicle sits lower, which matters for garage clearance by an inch or two.`,
      ],
      rows.length + Math.round(L),
    ),
  });
  const src = sourcing(rows as any);
  if (src) cover.push(src);

  return [
    ...section(`Length: ${n1(L)} inches`, lead),
    ...section("Proportions and how they changed", change),
    ...section("Coverage and measurement caveats", cover),
  ];
}

/* ------------------------------------------------------------------ */
/* Safety ratings                                                      */
/* ------------------------------------------------------------------ */

function starNum(v: string | null): number | null {
  const n = Number.parseInt(v ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function safety(input: DepthInput): NarrativeSection[] {
  const rows = input.safety ?? [];
  if (rows.length === 0) return [];
  const name = input.fullName;
  const sorted = [...rows].sort((a, b) => b.year - a.year);
  const latest = sorted[0]!;
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const overall = starNum(latest.overall_rating);
  const front = starNum(latest.overall_front_rating);
  const side = starNum(latest.overall_side_rating);
  const roll = starNum(latest.rollover_rating);
  const sharePct = Math.round((SAFETY_COHORT.fiveStar / SAFETY_COHORT.rated) * 100);

  const lead: Fact[] = [];
  lead.push({
    id: "overall",
    text: overall
      ? variant(
          [
            `NHTSA rates the ${latest.year} ${name} at ${overall} stars overall.`,
            `${overall} stars overall for the ${latest.year} ${name} in NHTSA's New Car Assessment Program.`,
            `The ${name} carries an ${overall}-star NHTSA overall score on its ${latest.year} test.`,
          ],
          latest.year + rows.length * 4 + (front ?? 0) + (side ?? 0) * 3,
        )
      : `NHTSA has published no overall star rating for the ${latest.year} ${name}; the tested sub-scores below are the record.`,
  });
  if (overall === 5) {
    lead.push({
      id: "context-5",
      text: variant(
        [
          `Five stars is the norm rather than a distinction: ${SAFETY_COHORT.fiveStar} of the ${SAFETY_COHORT.rated} rated models in our data score the same. Read the sub-scores, not the headline.`,
          `${sharePct}% of the rated models we hold also take five stars, so the headline separates nothing. The ${(front ?? side ?? 5) < 5 ? "sub-score below five" : "rollover figure"} is the number that distinguishes this one.`,
          `Treat five stars as the entry fee. ${SAFETY_COHORT.rated - SAFETY_COHORT.fiveStar} of ${SAFETY_COHORT.rated} rated models fall short of it; the useful comparison is between the ${rows.length} tested configurations of this model, not against the cohort.`,
        ],
        rows.length * 3 + years.length + (front ?? 0) * 5 + (side ?? 0) * 7 + Math.round((latest.rollover_possibility ?? 0) * 100),
      ),
    });
  } else if (overall && overall <= 3) {
    lead.push({
      id: "context-low",
      text: `That is a genuine outlier downward — ${sharePct}% of rated models here manage five stars, so a ${overall}-star result reflects a specific structural or rollover weakness rather than a hard test.`,
    });
  } else if (overall === 4) {
    lead.push({
      id: "context-4",
      text: `Four stars puts it behind the ${sharePct}% of rated models in our data that take five. The gap is almost always one sub-test, not the whole car.`,
    });
  }
  const subs: string[] = [];
  if (front) subs.push(`${front} frontal`);
  if (side) subs.push(`${side} side`);
  if (roll) subs.push(`${roll} rollover`);
  if (subs.length) {
    lead.push({
      id: "subs",
      text: variant(
        [
          `Sub-scores: ${subs.join(", ")}.`,
          `Broken out by test: ${subs.join(", ")}.`,
          `The individual results are ${subs.join(", ")}.`,
        ],
        (front ?? 0) * 13 + (side ?? 0) * 7 + (roll ?? 0) * 5 + rows.length,
      ) + (front && side && front < side ? ` The frontal result is the weak one — that is the crash type that kills most occupants.` : side && front && side < front ? ` Side impact is the weaker score, which matters most in urban junction crashes.` : ``),
    });
  }
  if (latest.rollover_possibility != null) {
    const rp = latest.rollover_possibility;
    const pctVal = rp <= 1 ? rp * 100 : rp;
    lead.push({
      id: "rollover",
      text: variant(
        [
          `Calculated rollover risk is ${n1(pctVal)}% — ${pctVal >= 20 ? "high-riding SUV and van territory" : pctVal >= 12 ? "typical for a crossover" : "car-like, from a low centre of gravity"}.`,
          `NHTSA puts the chance of a rollover in a single-vehicle crash at ${n1(pctVal)}%, ${pctVal >= 20 ? "which is body-on-frame SUV territory" : pctVal >= 12 ? "in line with unibody crossovers" : "closer to a sedan than an SUV"}.`,
          `Rollover probability: ${n1(pctVal)}%. ${pctVal >= 20 ? "Loading the roof rack makes that worse, measurably." : "Stability control does most of the work keeping that figure down."}`,
        ],
        Math.round(pctVal * 10) + rows.length + latest.year,
      ),
    });
  }

  const risk: Fact[] = [];
  const recalls = latest.recalls_count ?? 0;
  const complaints = latest.nhtsa_complaints_count ?? 0;
  const investigations = latest.investigation_count ?? 0;
  if (recalls || complaints || investigations) {
    risk.push({
      id: "counts",
      text: `${recalls} recall${recalls === 1 ? "" : "s"}, ${complaints} owner complaint${complaints === 1 ? "" : "s"} and ${investigations} open investigation${investigations === 1 ? "" : "s"} on the ${latest.year} car.`,
    });
    risk.push(
      complaints > 300
        ? {
            id: "complaints-high",
            text: `A complaint count that high is worth reading, not just counting — the pattern in the text tells you whether it is one systemic defect or scattered noise.`,
          }
        : recalls >= 5
          ? {
              id: "recalls-high",
              text: `${recalls} recalls is above average for a single model year. That cuts both ways: it means defects were found and fixed, but check the VIN for outstanding work before buying.`,
            }
          : {
              id: "counts-low",
              text: `That is a quiet record by NHTSA standards. Recalls are free to have done regardless of ownership history — verify by VIN rather than trusting a seller.`,
            },
    );
  }
  const assist: string[] = [];
  if (latest.has_esc) assist.push("stability control");
  if (latest.has_forward_collision_warning) assist.push("forward collision warning");
  if (latest.has_lane_departure_warning) assist.push("lane departure warning");
  risk.push({
    id: "assist",
    text: assist.length
      ? `Driver assistance on record: ${assist.join(", ")}. ` +
        variant(
          [
            `Availability means it was fitted or offered that year, not that every car has it — check the specific trim.`,
            `NHTSA records these as offered, so a base trim from the same year may have none of them. Confirm on the window sticker or by VIN.`,
            `These flags cover ${assist.length} system${assist.length === 1 ? "" : "s"} only; blind-spot monitoring and automatic emergency braking are tracked separately and are not implied here.`,
          ],
          assist.length * 11 + rows.length + latest.year,
        )
      : variant(
          [
            `NHTSA holds no driver-assistance flags for this model year, which is normal for cars predating the agency's reporting of those systems.`,
            `No assistance systems are recorded against this test. Absence in the database is not proof of absence on the car — verify by trim.`,
            `The agency logged no electronic-aid data here, so judge this one on the crash results alone.`,
          ],
          rows.length * 9 + latest.year,
        ),
  });

  const cover: Fact[] = [];
  cover.push({
    id: "count",
    text: variant(
      [
        `${rows.length} tested configuration${rows.length === 1 ? "" : "s"} across ${years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : years[0]}.`,
        `Our record holds ${rows.length} NHTSA test entr${rows.length === 1 ? "y" : "ies"} for this nameplate, ${years.length > 1 ? `spanning ${years[years.length - 1]! - years[0]!} model years` : `a single model year (${years[0]})`}.`,
        `${years.length} model year${years.length === 1 ? "" : "s"} of NHTSA data, ${rows.length} row${rows.length === 1 ? "" : "s"} in total.`,
      ],
      rows.length + years.length * 5 + (overall ?? 0),
    ),
  });
  cover.push({
    id: "caveat",
    text: variant(
      [
        `Stars are awarded against the protocol in force that model year. NHTSA tightened its frontal and side tests in 2011, so a five-star 2009 result is not a five-star 2024 result.`,
        `NHTSA tests a specific body style and drivetrain, then applies the score to the range. A different cab, wheelbase or drive layout may not have been crashed at all.`,
        `These are NHTSA figures only. IIHS runs different tests — small-overlap front and updated side impact — and a car can do well in one programme and poorly in the other.`,
      ],
      rows.length + years.length,
    ),
  });
  const src = sourcing(rows as any);
  if (src) cover.push(src);

  return [
    ...section(overall ? `NHTSA overall: ${overall} stars` : `NHTSA crash-test record`, lead),
    ...section("Recalls, complaints and equipment", risk),
    ...section("How to read this rating", cover),
  ];
}

export function buildDepthNarrative(input: DepthInput): NarrativeSection[] {
  if (!isDepthScoped(input.dataType, input.makeSlug, input.modelSlug)) return [];
  switch (input.dataType) {
    case "towing-capacity":
      return towing(input);
    case "mpg":
      return mpg(input);
    case "bed-dimensions":
      return bedDimensions(input);
    case "fuel-tank-capacity":
      return fuelTank(input);
    case "dimensions":
      return dimensions(input);
    case "safety-ratings":
      return safety(input);
    default:
      return [];
  }
}
